import { useEffect, useState } from "react";
import { Button, Card, Drawer, EmptyState, Hairline } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { BrandDTO, BrandWithCountDTO } from "@wmart/shared-types";
import { BrandEditDrawer } from "../components/BrandEditDrawer.js";

const API_BASE: string = (import.meta.env.VITE_API_URL as string) ?? "";

/**
 * Flat brand management — list every live brand with its product count, plus
 * create / edit / merge / delete. Merge is the de-dup tool: it reassigns every
 * product on the source brand to a target, then soft-deletes the source.
 */
export function BrandsList() {
  const { notify, notifyProblem } = useToast();
  const [brands, setBrands] = useState<BrandWithCountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BrandDTO | null>(null);
  const [creating, setCreating] = useState(false);
  const [mergeSource, setMergeSource] = useState<BrandWithCountDTO | null>(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get<{ brands: BrandWithCountDTO[] }>(
        "/v1/admin/brands",
      );
      setBrands(res.brands);
    } catch {
      notify("error", "Failed to load brands");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(b: BrandWithCountDTO) {
    if (!window.confirm(`Delete brand "${b.title}"?`)) return;
    try {
      await api.delete(`/v1/admin/brands/${b.id}`);
      notify("success", "Brand deleted");
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Delete failed");
    }
  }

  async function confirmMerge() {
    if (!mergeSource || !mergeTarget) return;
    setBusy(true);
    try {
      const res = await api.post<{ reassigned: number }>(
        `/v1/admin/brands/${mergeSource.id}/merge`,
        { into_id: mergeTarget },
      );
      notify("success", `Merged — ${res.reassigned} products reassigned`);
      setMergeSource(null);
      setMergeTarget("");
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Merge failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">Brands</h1>
        <Button onClick={() => setCreating(true)}>New brand</Button>
      </div>

      {loading ? (
        <p className="text-body text-ink-500">Loading…</p>
      ) : brands.length === 0 ? (
        <EmptyState
          icon="package"
          title="No brands yet"
          body="Create a brand, or run the backfill to import them from products."
        />
      ) : (
        <Card className="divide-y divide-ink-100">
          {brands.map((b) => (
            <div key={b.id} className="flex items-center gap-4 px-4 py-3">
              {b.logo_key ? (
                <img
                  src={`${API_BASE}/v1/admin/images/${b.logo_key}`}
                  alt=""
                  className="h-9 w-9 rounded-sm border border-ink-100 bg-white object-contain"
                />
              ) : (
                <div
                  className="h-9 w-9 rounded-sm border border-dashed border-ink-200"
                  aria-hidden
                />
              )}
              <div className="flex-1">
                <p className="text-body font-medium text-ink-900">{b.title}</p>
                <p className="font-mono text-caption text-ink-500">{b.slug}</p>
              </div>
              <span className="text-body-sm text-ink-500">
                {b.product_count} products
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing(b)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setMergeSource(b);
                    setMergeTarget("");
                  }}
                >
                  Merge
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void remove(b)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {creating ? (
        <BrandEditDrawer
          brand={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      ) : null}
      {editing ? (
        <BrandEditDrawer
          brand={editing}
          onClose={() => {
            setEditing(null);
            void load();
          }}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}

      {mergeSource ? (
        <Drawer
          open
          onClose={() => setMergeSource(null)}
          title={`Merge ${mergeSource.title}`}
        >
          <div className="flex flex-col gap-4 p-6">
            <p className="text-body-sm text-ink-700">
              Move all {mergeSource.product_count} products from{" "}
              <strong>{mergeSource.title}</strong> into another brand, then
              delete it. This cannot be undone.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-body-sm text-ink-700">Merge into</span>
              <select
                className="rounded-sm border border-ink-200 px-3 py-2"
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
              >
                <option value="">Choose a brand…</option>
                {brands
                  .filter((x) => x.id !== mergeSource.id)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.title}
                    </option>
                  ))}
              </select>
            </label>
            <Hairline />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setMergeSource(null)}>
                Cancel
              </Button>
              <Button
                disabled={!mergeTarget || busy}
                loading={busy}
                onClick={() => void confirmMerge()}
              >
                Merge
              </Button>
            </div>
          </div>
        </Drawer>
      ) : null}
    </div>
  );
}
